(() => {
  if (window.ttHomepageLoaded) return;
  window.ttHomepageLoaded = true;
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)');

  class TTHeroSlider extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-go]')];
      this.index = 0;
      this.visible = true;
      this.reduced = motion();
      this.playing = this.dataset.autoplay === 'true' && !this.reduced.matches && !window.Shopify?.designMode;
      const controls = this.querySelector('[data-slider-controls]');
      if (!controls || this.slides.length < 2) return;
      controls.hidden = false;
      this.querySelector('[data-next]').addEventListener('click', () => this.interact(this.index + 1), { signal });
      this.querySelector('[data-prev]').addEventListener('click', () => this.interact(this.index - 1), { signal });
      this.dots.forEach(dot => dot.addEventListener('click', () => this.interact(Number(dot.dataset.go)), { signal }));
      this.querySelector('[data-play]').addEventListener('click', () => {
        this.playing = !this.playing && !this.reduced.matches;
        this.schedule();
      }, { signal });
      this.addEventListener('focusin', () => this.pause(), { signal });
      this.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') this.pause(); }, { signal });
      this.addEventListener('keydown', event => {
        if (event.target.matches('input, textarea, select')) return;
        const destination = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 }[event.key];
        if (destination === undefined) return;
        event.preventDefault();
        this.interact(destination);
      }, { signal });
      this.addEventListener('pointerdown', event => {
        if (event.target.closest('a, button, input')) return;
        this.startPoint = { x: event.clientX, y: event.clientY };
      }, { signal, passive: true });
      this.addEventListener('pointerup', event => {
        if (!this.startPoint) return;
        const dx = event.clientX - this.startPoint.x;
        const dy = event.clientY - this.startPoint.y;
        this.startPoint = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) this.interact(this.index + (dx < 0 ? 1 : -1));
      }, { signal, passive: true });
      this.addEventListener('pointercancel', () => { this.startPoint = null; }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.reduced.addEventListener('change', () => { if (this.reduced.matches) this.playing = false; this.schedule(); }, { signal });
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.interact(index);
      }, { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); }, { threshold: .15 });
        this.observer.observe(this);
      }
      this.show(0, false);
    }
    disconnectedCallback() { clearTimeout(this.timer); this.abort?.abort(); this.observer?.disconnect(); }
    pause() { this.playing = false; this.schedule(); }
    interact(index) { this.playing = false; this.show(index, true); }
    show(index, announce) {
      this.index = (index + this.slides.length) % this.slides.length;
      const focusedSlide = this.slides.find(slide => slide.contains(document.activeElement));
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; slide.inert = i !== this.index; });
      this.dots.forEach((dot, i) => { if (i === this.index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
      if (focusedSlide?.hidden) { this.tabIndex = -1; this.focus({ preventScroll: true }); }
      if (announce) this.querySelector('[data-slide-status]').textContent = `Slide ${this.index + 1} of ${this.slides.length}: ${this.slides[this.index].querySelector('h1, h2').textContent}`;
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      const button = this.querySelector('[data-play]');
      if (!button) return;
      button.textContent = this.playing ? 'Pause' : 'Play';
      button.setAttribute('aria-label', this.playing ? 'Pause automatic slide changes' : 'Start automatic slide changes');
      button.setAttribute('aria-pressed', String(this.playing));
      button.disabled = this.reduced.matches;
      if (this.playing && this.visible && !document.hidden && !this.reduced.matches) {
        this.timer = setTimeout(() => this.show(this.index + 1, false), Number(this.dataset.interval) || 7000);
      }
    }
  }

  class TTOfferMarquee extends HTMLElement {
    connectedCallback() {
      const source = this.querySelector('[data-marquee-source]');
      if (!source || !this.hasAttribute('data-animate') || this.hasAttribute('data-ready')) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.clone = source.cloneNode(true);
      this.clone.removeAttribute('data-marquee-source');
      this.clone.querySelectorAll('[data-shopify-editor-block]').forEach(element => element.removeAttribute('data-shopify-editor-block'));
      this.clone.setAttribute('aria-hidden', 'true');
      this.clone.inert = true;
      this.clone.querySelectorAll('a').forEach(link => { link.removeAttribute('href'); link.tabIndex = -1; });
      source.after(this.clone);
      this.setAttribute('data-ready', '');
      const button = this.querySelector('[data-marquee-toggle]');
      button.hidden = false;
      button.addEventListener('click', () => {
        const paused = this.toggleAttribute('data-paused');
        button.textContent = paused ? 'Play' : 'Pause';
        button.setAttribute('aria-pressed', String(paused));
        button.setAttribute('aria-label', paused ? 'Play promotional messages' : 'Pause promotional messages');
      }, { signal });
      document.addEventListener('visibilitychange', () => this.toggleAttribute('data-tab-hidden', document.hidden), { signal });
    }
    disconnectedCallback() { this.abort?.abort(); this.clone?.remove(); this.removeAttribute('data-ready'); }
  }

  class TTVideoStory extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.reduced = motion();
      this.visible = false;
      this.userPaused = false;
      this.video.setAttribute('playsinline', '');
      const poster = this.querySelector('[data-poster-url]');
      if (poster) this.video.poster = poster.content.textContent.trim();
      this.button = this.querySelector('[data-video-toggle]');
      this.button.hidden = false;
      const update = () => {
        const playing = !this.video.paused && !this.video.ended;
        this.button.textContent = playing ? 'Pause film' : 'Play film';
        this.button.setAttribute('aria-label', this.button.textContent);
      };
      this.button.addEventListener('click', () => {
        if (this.video.paused) { this.userPaused = false; this.video.play().catch(update); }
        else { this.userPaused = true; this.video.pause(); }
      }, { signal });
      this.video.addEventListener('play', update, { signal });
      this.video.addEventListener('pause', () => { if (this.visible && !document.hidden && !this.reduced.matches) this.userPaused = true; update(); }, { signal });
      this.video.addEventListener('ended', update, { signal });
      this.video.addEventListener('error', () => { this.button.hidden = true; }, { signal });
      const visibility = () => {
        if (!this.visible || document.hidden) this.video.pause();
        else if (this.dataset.autoplay === 'true' && !this.reduced.matches && !this.userPaused && !window.Shopify?.designMode) {
          this.video.muted = true;
          this.video.play().catch(update);
        }
      };
      document.addEventListener('visibilitychange', visibility, { signal });
      this.reduced.addEventListener('change', () => { if (this.reduced.matches && this.dataset.autoplay === 'true') this.video.pause(); }, { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; visibility(); }, { threshold: .25 });
        this.observer.observe(this);
      }
      update();
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.video?.pause(); }
  }
  if (!customElements.get('tt-hero-slider')) customElements.define('tt-hero-slider', TTHeroSlider);
  if (!customElements.get('tt-offer-marquee')) customElements.define('tt-offer-marquee', TTOfferMarquee);
  if (!customElements.get('tt-video-story')) customElements.define('tt-video-story', TTVideoStory);

  // Reopen Shopify's own preference modal; never register or change a consent decision here.
  const prepareCookiePreferences = () => {
    const available = typeof window.privacyBanner?.showPreferences === 'function';
    document.querySelectorAll('[data-tt-cookie-preferences]').forEach(button => { button.hidden = !available; });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prepareCookiePreferences, { once: true });
  else prepareCookiePreferences();
  window.addEventListener('load', prepareCookiePreferences, { once: true });
  document.addEventListener('shopify:section:load', prepareCookiePreferences);
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-tt-cookie-preferences]');
    if (!button || typeof window.privacyBanner?.showPreferences !== 'function') return;
    const status = button.parentElement.querySelector('[data-tt-cookie-status]');
    status.textContent = '';
    button.disabled = true;
    try { await window.privacyBanner.showPreferences(); }
    catch { status.textContent = 'Cookie preferences are temporarily unavailable. Please try again.'; }
    finally { button.disabled = false; }
  });
})();
